import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { CampaignCreateOption } from '../api';
import {
  useSetCampaignPresetFavoriteApi,
  useUnsetCampaignPresetFavoriteApi,
} from 'src/api/campaignPresets';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useCampaignConfigService } from 'src/modules/campaignConfigBuilder/services';
import { setConfigFavorite } from 'src/modules/campaignConfigBuilder/slices';
import { setCreateOptionFavorite } from '../slices';

// Favorites live on two slices (start options and configs). Toggling a start
// option keeps both in sync so the picker and builder reflect the same state.
export const useToggleCampaignCreateOptionFavorite = () => {
  const dispatch = useDispatch();
  const currentCompanyId = useSelector(getCurrentCompanyId);
  const { setCampaignConfigFavorite, unsetCampaignConfigFavorite } = useCampaignConfigService();
  const setCampaignPresetFavorite = useSetCampaignPresetFavoriteApi();
  const unsetCampaignPresetFavorite = useUnsetCampaignPresetFavoriteApi();
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  // Synchronous in-flight guard. State updates are async, so two clicks in the
  // same tick would both pass a state-based check and double-fire the request;
  // a ref is updated immediately. The state Set only drives the pending UI.
  const inFlightKeys = useRef<Set<string>>(new Set());

  const keyOf = (option: CampaignCreateOption) => `${option.kind}:${option.id}`;

  const toggleFavorite = useCallback(async (option: CampaignCreateOption) => {
    if (!currentCompanyId) {
      toast.error('Unable to update favorites: no company selected');
      return;
    }

    const pendingKey = keyOf(option);
    if (inFlightKeys.current.has(pendingKey)) {
      return;
    }
    inFlightKeys.current.add(pendingKey);

    const previous = option.favorite;
    const next = !previous;

    setPendingIds(prev => new Set(prev).add(pendingKey));

    const configId = option.config?.id;
    const presetId = option.preset?.id;

    // Optimistic update across every slice that tracks this favorite.
    dispatch(setCreateOptionFavorite({ kind: option.kind, id: option.id, favorite: next }));
    if (option.kind === 'config' && configId) {
      dispatch(setConfigFavorite({ id: configId, favorite: next }));
    }

    try {
      if (option.kind === 'config' && configId) {
        if (next) {
          await setCampaignConfigFavorite(configId, currentCompanyId);
        } else {
          await unsetCampaignConfigFavorite(configId, currentCompanyId);
        }
      } else if (option.kind === 'preset' && presetId) {
        if (next) {
          await setCampaignPresetFavorite(presetId, currentCompanyId);
        } else {
          await unsetCampaignPresetFavorite(presetId, currentCompanyId);
        }
      }
    } catch (error) {
      // Roll back on failure.
      dispatch(setCreateOptionFavorite({ kind: option.kind, id: option.id, favorite: previous }));
      if (option.kind === 'config' && configId) {
        dispatch(setConfigFavorite({ id: configId, favorite: previous }));
      }
      toast.error('Failed to update favorites');
      console.error('Failed to toggle campaign start option favorite:', error);
    } finally {
      inFlightKeys.current.delete(pendingKey);
      setPendingIds(prev => {
        const updated = new Set(prev);
        updated.delete(pendingKey);
        return updated;
      });
    }
  }, [
    currentCompanyId,
    dispatch,
    setCampaignConfigFavorite,
    unsetCampaignConfigFavorite,
    setCampaignPresetFavorite,
    unsetCampaignPresetFavorite,
  ]);

  return {
    toggleFavorite,
    pendingIds,
  };
};
